import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { ethers } from 'ethers';
import { logError, logInfo } from '@utils/log';
import { fundRaisin } from '@contracts/fundraisin';
import errorConfig from '@config/error.json';
import { browserName } from 'react-device-detect';
import { useThemeContext } from '@context/ThemeContext';
import { useApiClient } from '@hooks/useApiClient';

// Action types for wallet state management
export const WalletActionTypes = {
  SET_WALLET_LOADING: 'SET_WALLET_LOADING',
  SET_CONNECTION_STATE: 'SET_CONNECTION_STATE',
  SET_NETWORK_DATA: 'SET_NETWORK_DATA',
  SET_BALANCE: 'SET_BALANCE',
  RESET_WALLET: 'RESET_WALLET',
};

// Initial wallet state
const initialWalletState = {
  // Connection state
  wallet: {
    address: 0,
    isConnected: false,
  },
  walletAddress: 0,
  isWalletConnected: false,

  // Loading states
  loading: false,
  walletLoading: false,

  // Error handling
  error: null,
  walletError: null,

  // Network information
  network: null,
  chainId: null,

  // Balance information
  balance: null,
  formattedBalance: '0.00',

  // Providers
  etherumProvider: null,
  jsonProvider: null,
  signer: null,

  // Additional wallet data
  walletData: null,
};

// Wallet reducer with comprehensive state management
const walletReducer = (state, action) => {
  switch (action.type) {
    case WalletActionTypes.SET_WALLET_LOADING:
      return {
        ...state,
        loading: action.loading,
        walletLoading: action.loading
      };

    case WalletActionTypes.SET_CONNECTION_STATE:
      return {
        ...state,
        wallet: action.wallet,
        walletAddress: action.wallet.address,
        isWalletConnected: action.wallet.isConnected,
        loading: false,
        walletLoading: false,
        error: null,
        walletError: null
      };

    case WalletActionTypes.SET_NETWORK_DATA:
      return {
        ...state,
        network: action.network,
        chainId: action.chainId,
        etherumProvider: action.etherumProvider,
        jsonProvider: action.jsonProvider,
        signer: action.signer
      };

    case WalletActionTypes.SET_BALANCE:
      return {
        ...state,
        balance: action.balance,
        formattedBalance: action.formattedBalance
      };

    case WalletActionTypes.RESET_WALLET:
      return {
        ...initialWalletState,
        // Keep providers for reconnection
        etherumProvider: state.etherumProvider,
        jsonProvider: state.jsonProvider,
      };

    default:
      return state;
  }
};

// Wallet Context
const WalletContext = createContext(null);

// Wallet Provider with advanced web3 functionality
export const WalletProvider = ({ children }) => {
  const { get } = useApiClient();
  const [state, dispatch] = useReducer(walletReducer, initialWalletState);
  const { constants, setError } = useThemeContext();

  // Refs for cleanup and preventing memory leaks
  const cleanupFunctionsRef = useRef([]);
  const balanceIntervalRef = useRef(null);

  // Initialize providers with proper error handling
  const initializeProviders = useCallback(() => {
    try {
      let etherumProvider = null;
      let jsonProvider = null;

      // Initialize ethereum provider - support multiple wallet types
      if (typeof window !== 'undefined') {
        let provider = null;

        // Check for Core Wallet first (it has priority on Avalanche)
        if (window.avalanche && window.avalanche.isAvalanche) {
          provider = window.avalanche;
          logInfo('Using Core Wallet provider');
        }
        // Fallback to standard ethereum provider (MetaMask, etc.)
        else if (window.ethereum) {
          provider = window.ethereum;
          logInfo('Using MetaMask or compatible provider');
        }

        if (provider) {
          etherumProvider = new ethers.providers.Web3Provider(provider, 'any');
          etherumProvider.pollingInterval = 12000;
        }
      }

      // Initialize JSON RPC provider with fallback
      const networkUrl = (constants && constants.NETWORK_URL) || 'https://api.avax.network/ext/bc/C/rpc';
      jsonProvider = new ethers.providers.JsonRpcProvider(networkUrl);

      dispatch({
        type: WalletActionTypes.SET_NETWORK_DATA,
        etherumProvider,
        jsonProvider,
        network: null,
        chainId: null,
        signer: null
      });

      return { etherumProvider, jsonProvider };
    } catch (error) {
      logError('Provider initialization failed:', error);
      return { etherumProvider: null, jsonProvider: null };
    }
  }, [constants]);

  // Browser compatibility check
  const checkBrowser = useCallback(() => {
    if (browserName === "Firefox") {
      const errorMsg = "Fund Raisin sites are currently only supported on Chrome and Brave browsers on desktop and in Metamask browser on mobile.";
      return false;
    }
    return true;
  }, []);

  // Network validation
  const validateNetwork = useCallback(async (provider) => {
    try {
      const network = await provider.getNetwork();

      // Try to get network config, fallback to Avalanche
      let networkConfig = { chainId: 43114, name: 'Avalanche' };
      try {
        networkConfig = await get('/network');
      } catch (fetchError) {
        logInfo('Using fallback network config');
      }

      if (parseInt(network.chainId) !== parseInt(networkConfig.chainId)) {
        throw new Error(`Network not supported: expected ${parseInt(networkConfig.chainId)} got ${parseInt(network.chainId)}`);
      }

      return { network, networkConfig };
    } catch (error) {
      throw new Error(`Network validation failed: ${error.message}`);
    }
  }, []);

  // Enhanced wallet connection with comprehensive error handling
  const connectWallet = useCallback(async () => {
    if (!checkBrowser()) {
      return false;
    }

    // Check for available wallets (MetaMask, Core Wallet, etc.)
    const hasMetaMask = typeof window.ethereum !== 'undefined';
    const hasCoreWallet = typeof window.avalanche !== 'undefined';
    const hasEthereumProvider = hasMetaMask || hasCoreWallet;

    if (!hasEthereumProvider) {
      const errorMsg = 'No wallet found. Please install MetaMask or Core Wallet.';
      setError(errorMsg);
      return false;
    }

    dispatch({ type: WalletActionTypes.SET_WALLET_LOADING, loading: true });

    try {
      const { etherumProvider } = initializeProviders();

      if (!etherumProvider) {
        throw new Error('Failed to initialize ethereum provider');
      }

      // Request account access
      await etherumProvider.send("eth_requestAccounts", []);

      // Validate network
      const { network } = await validateNetwork(etherumProvider);

      // Get signer and address
      const signer = etherumProvider.getSigner();
      const address = await signer.getAddress();

      // Update network data
      dispatch({
        type: WalletActionTypes.SET_NETWORK_DATA,
        network,
        chainId: network.chainId,
        etherumProvider,
        jsonProvider: state.jsonProvider,
        signer
      });

      // Initialize contract if constants are available
      if (constants && constants.contract && constants.contract.fundraisin) {
        try {
          await fundRaisin.initializeWithSigner(
            constants.contract.fundraisin,
            etherumProvider,
            address
          );

          logInfo('Fund Raisin contract initialized successfully for address:', address);
        } catch (contractError) {
          logError('Contract initialization failed:', contractError);
          // Don't fail wallet connection if contract init fails
        }
      } else {
        logInfo('Contract constants not available, skipping contract initialization');
        logInfo('Available constants:', constants);
      }

      // Update connection state
      const walletData = {
        address,
        isConnected: true,
      };

      dispatch({
        type: WalletActionTypes.SET_CONNECTION_STATE,
        wallet: walletData
      });

      // Start balance monitoring
      updateBalance(address, etherumProvider);

      logInfo(`Wallet connected successfully: ${address}`);
      return true;

    } catch (error) {
      logError('Wallet connection failed:', error);

      let errorMsg = 'Failed to connect wallet';
      if (error.message.includes('already pending')) {
        errorMsg = '';
      } else if (error.message.includes('User rejected')) {
        errorMsg = 'Connection rejected by user';
      } else if (error.message.includes('Network not supported')) {
        errorMsg = error.message;
      } else if (!window.ethereum) {
        errorMsg = errorConfig.INSTALL_METAMASK || 'Please install MetaMask';
      }

      setError(errorMsg);
      return false;
    }
  }, [constants, checkBrowser, initializeProviders, validateNetwork, state.jsonProvider]);

  // Enhanced disconnect with proper cleanup
  const disconnectWallet = useCallback(() => {
    // Clear balance update interval
    if (balanceIntervalRef.current) {
      clearInterval(balanceIntervalRef.current);
      balanceIntervalRef.current = null;
    }

    // Reset contract state
    try {
      fundRaisin.reset();
      logInfo('Contract state reset');
    } catch (error) {
      logError('Failed to reset contract state:', error);
    }

    // Execute cleanup functions
    cleanupFunctionsRef.current.forEach(cleanup => {
      if (typeof cleanup === 'function') {
        try {
          cleanup();
        } catch (error) {
          logError('Cleanup function error:', error);
        }
      }
    });
    cleanupFunctionsRef.current = [];

    // Reset wallet state
    dispatch({ type: WalletActionTypes.RESET_WALLET });

    logInfo('Wallet disconnected and cleaned up');
  }, []);

  // Update wallet address (reconnection logic with contract signer update)
  const updateWalletAddress = useCallback(async (newAddress = null) => {
    if (!state.etherumProvider) {
      initializeProviders();
      return;
    }

    try {
      const signer = state.etherumProvider.getSigner();
      const address = newAddress || await signer.getAddress();

      if (address && address !== state.walletAddress) {
        // Update contract signer for new address
        try {
          await fundRaisin.updateSigner(state.etherumProvider, address);
          logInfo('Contract signer updated for new address:', address);
        } catch (contractError) {
          logError('Failed to update contract signer for new address:', contractError);
        }

        // Update wallet state
        const walletData = {
          address,
          isConnected: true,
        };

        dispatch({
          type: WalletActionTypes.SET_CONNECTION_STATE,
          wallet: walletData
        });

        // Start balance monitoring for new address
        updateBalance(address, state.etherumProvider);

        logInfo(`Wallet address updated to: ${address}`);
      }
    } catch (error) {
      logError('Failed to update wallet address:', error);
      disconnectWallet();
    }
  }, [state.etherumProvider, state.walletAddress, disconnectWallet, initializeProviders]);

  // Wallet validation
  const isValidWallet = useCallback(() => {
    return state.isWalletConnected &&
      state.walletAddress &&
      state.walletAddress !== 0 &&
      state.etherumProvider !== null;
  }, [state.isWalletConnected, state.walletAddress, state.etherumProvider]);

  // Balance management
  const updateBalance = useCallback(async (address, provider) => {
    if (!address || !provider) return;

    try {
      const balance = await provider.getBalance(address);
      const formattedBalance = ethers.utils.formatEther(balance);

      dispatch({
        type: WalletActionTypes.SET_BALANCE,
        balance,
        formattedBalance: parseFloat(formattedBalance).toFixed(4)
      });
    } catch (error) {
      logError('Failed to update balance:', error);
    }
  }, []);

  // Manual address setter (for development/testing)
  const setManualWalletAddress = useCallback((address) => {
    const walletData = {
      address: address || 0,
      isConnected: !!address && address !== 0,
    };

    dispatch({
      type: WalletActionTypes.SET_CONNECTION_STATE,
      wallet: walletData
    });
  }, []);

  // Initialize providers and set up auto-connect callback on mount
  useEffect(() => {
    initializeProviders();

    // Register connect wallet callback for auto-connect functionality
    fundRaisin.setConnectWalletCallback(connectWallet);

    return () => {
      // Clear callback on unmount
      fundRaisin.setConnectWalletCallback(null);
    };
  }, [initializeProviders]);

  // Setup event listeners with proper cleanup
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else {
        updateWalletAddress();
      }
    };

    const handleChainChanged = () => {
      logInfo('Chain changed, updating wallet address');
      updateWalletAddress();
    };

    const handleConnect = (connectInfo) => {
      logInfo('Wallet connected via event:', connectInfo);
    };

    const handleDisconnect = (error) => {
      logInfo('Wallet disconnected via event:', error);
      disconnectWallet();
    };

    // Add event listeners - support both MetaMask and Core Wallet
    const walletProvider = window.avalanche?.isAvalanche ? window.avalanche : window.ethereum;

    if (walletProvider && walletProvider.on) {
      walletProvider.on('accountsChanged', handleAccountsChanged);
      walletProvider.on('chainChanged', handleChainChanged);
      walletProvider.on('connect', handleConnect);
      walletProvider.on('disconnect', handleDisconnect);
    }

    // Store cleanup function
    const cleanup = () => {
      if (walletProvider && walletProvider.removeListener) {
        walletProvider.removeListener('accountsChanged', handleAccountsChanged);
        walletProvider.removeListener('chainChanged', handleChainChanged);
        walletProvider.removeListener('connect', handleConnect);
        walletProvider.removeListener('disconnect', handleDisconnect);
      }
    };
    cleanupFunctionsRef.current.push(cleanup);

    return cleanup;
  }, [updateWalletAddress, disconnectWallet]);

  // Balance monitoring for connected wallets
  useEffect(() => {
    if (isValidWallet() && state.walletAddress && state.etherumProvider) {
      // Update balance immediately
      updateBalance(state.walletAddress, state.etherumProvider);

      // Set up periodic balance updates
      balanceIntervalRef.current = setInterval(() => {
        updateBalance(state.walletAddress, state.etherumProvider);
      }, 30000); // Update every 30 seconds

      return () => {
        if (balanceIntervalRef.current) {
          clearInterval(balanceIntervalRef.current);
          balanceIntervalRef.current = null;
        }
      };
    }
  }, [isValidWallet, state.walletAddress, state.etherumProvider, updateBalance]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (balanceIntervalRef.current) {
        clearInterval(balanceIntervalRef.current);
      }
      cleanupFunctionsRef.current.forEach(cleanup => {
        if (typeof cleanup === 'function') {
          try {
            cleanup();
          } catch (error) {
            logError('Cleanup error on unmount:', error);
          }
        }
      });
    };
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    // Wallet state
    ...state,
    wallet: state.wallet,
    walletAddress: state.walletAddress,
    isWalletConnected: state.isWalletConnected,

    // Loading states
    loading: state.loading,
    walletLoading: state.walletLoading,


    // Network data
    network: state.network,
    chainId: state.chainId,

    // Balance data
    balance: state.balance,
    formattedBalance: state.formattedBalance,

    // Providers
    etherumProvider: state.etherumProvider,
    jsonProvider: state.jsonProvider,
    signer: state.signer,

    // Wallet actions
    connectWallet,
    disconnectWallet,
    updateWalletAddress,
    isValidWallet,
    setManualWalletAddress,
    // Additional data
    walletData: state.walletData,
  }), [
    state,
    connectWallet,
    disconnectWallet,
    updateWalletAddress,
    isValidWallet,
    setManualWalletAddress
  ]);

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
};

// Custom hook to use wallet context
export const useWalletContext = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWalletContext must be used within a WalletProvider');
  }
  return context;
};

// Export wallet context for direct access if needed
export { WalletContext };
